/**
 * Gaussian elimination with partial pivoting, for the small symmetric systems the
 * two-way ANOVA solves (at most a few dozen unknowns).
 *
 * Returns null for a singular system rather than a vector of NaNs, so a caller cannot
 * print a confident number from a degenerate design.
 */
export function solveLinear(
  matrix: readonly (readonly number[])[],
  rhs: readonly number[]
): number[] | null {
  const n = rhs.length;
  const a = matrix.map((row, i) => [...row, rhs[i]]);
  let scale = 0;
  for (const row of matrix) {
    for (const value of row) {
      scale = Math.max(scale, Math.abs(value));
    }
  }
  const tiny = Math.max(scale, 1) * 1e-12;
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(a[row][col]) > Math.abs(a[pivot][col])) {
        pivot = row;
      }
    }
    if (Math.abs(a[pivot][col]) <= tiny) {
      return null;
    }
    [a[col], a[pivot]] = [a[pivot], a[col]];
    for (let row = col + 1; row < n; row++) {
      const factor = a[row][col] / a[col][col];
      for (let k = col; k <= n; k++) {
        a[row][k] -= factor * a[col][k];
      }
    }
  }
  const x = new Array<number>(n).fill(0);
  for (let row = n - 1; row >= 0; row--) {
    let total = a[row][n];
    for (let k = row + 1; k < n; k++) {
      total -= a[row][k] * x[k];
    }
    x[row] = total / a[row][row];
  }
  return x;
}

/** x′y. */
export function dot(x: readonly number[], y: readonly number[]): number {
  let total = 0;
  for (const [i, value] of x.entries()) {
    total += value * y[i];
  }
  return total;
}
